//@flow
import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import { Checkbox } from 'common/components/core/checkbox';
import classnames from 'classnames';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';
import ShiftActions from 'common/redux/actions/shiftMaster';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import ToastActions from 'common/redux/actions/toast';
import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import ShiftSelectors from 'common/redux/selectors/shiftMaster';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import {
    ConnectedTeamSelectorFilter,
    ConnectedTeamIdSelectorFilter
} from 'common/entity/components/TeamSelectorFilter';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import { withRouter } from 'common/components/core/router';
import { withUnitConverter } from 'common/containers/intl';
import SingleShiftEditContainer from './SingleShiftEditContainer';

import theme from 'common/entity/BulkEditContainer.scss';
import ShiftMasterImportContainer from './ShiftMasterImportContainer';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';

import { dashboardColors } from 'common/enums/ColorEnum';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import TeamList from 'common/components/core/common/TeamList';
import ShiftSlots from 'common/entity/components/ShiftSlotSummary';

import type { Shift } from 'common/models/internal/Shift';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { TFunction } from 'react-i18next';
import VisitUtils from 'common/utils/VisitUtils';
import shiftMasterIds from 'common/tests/masterManagement/shiftMaster.json';
import {
    ENTITY_TABLE_ID,
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID
} from 'common/constants/entity/shiftMaster';
import { RoleOperationEnum } from 'common/modules/rbac';
import DateTimeUtils, {
    DateInputFormats,
    TimeStyle
} from 'common/utils/DateTimeUtils';

const selectTeamId = ({ teamId }) => teamId;
const daysList = Object.values(DayOfWeekType);
const timeFormat = DateInputFormats.HOUR24_SHORT;

const getTeamIdFilter = ({ filteredColumn }) => {
    const {
        value: { allowEmptyOrNull, values }
    } = filteredColumn;
    return new FilterBuilder()
        .withName('teamId.teamId')
        .withOperation(FilterOperation.Equals)
        .withValues(values)
        .withAllowEmptyOrNull(allowEmptyOrNull)
        .withCaseSensitive(true);
};

const INITIAL_COLUMNS = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'shiftTable.id',
        accessor: 'id',
        frozen: true,
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'shiftTable.code',
        accessor: 'code',
        frozen: true,
        minWidth: 100,
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'shiftTable.name',
        accessor: 'name'
    },
    {
        headerTextId: 'shiftTable.teamName',
        id: 'teams.name',
        exportId: 'team.name',
        accessor: 'teamId',
        sortable: false,
        minWidth: 140,
        getExtraFilterProps: () => ({
            isMulti: false,
            disableBlanksSelectOption: true,
            showSelectFooter: false,
            submitOnOutsideClick: true
        }),
        FilterComponent: ConnectedTeamSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const { teamId } = (original: Shift);
            return teamId ? (
                <TeamList teams={teams} teamIds={[teamId]} theme={theme} />
            ) : null;
        }
    },
    {
        headerTextId: 'shiftTable.teamId',
        accessor: 'teamId',
        exportId: 'team.id',
        sortable: false,
        minWidth: 140,
        getExtraFilterProps: () => ({
            isMulti: false,
            disableBlanksSelectOption: true,
            showSelectFooter: false,
            submitOnOutsideClick: true,
            testId: shiftMasterIds.table.teamIdFilter
        }),
        FilterComponent: ConnectedTeamIdSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const { teamId } = (original: Shift);
            return teamId ? (
                <TeamList
                    teams={teams}
                    teamIds={[teamId]}
                    theme={theme}
                    selectTeamName={selectTeamId}
                />
            ) : null;
        }
    },
    {
        headerTextId: 'shiftTable.workingDays',
        id: 'workingDays',
        sortable: false,
        filterable: false,
        minWidth: 160,
        fieldId: 'workingDays',
        accessor: (data: Shift) => {
            const { workingDays } = data;
            const DaysOfWeekActive = [];
            workingDays.forEach(dayShift => {
                if (dayShift.slot) {
                    DaysOfWeekActive.push(dayShift.getShortCode());
                }
            });
            return DaysOfWeekActive.join(' ');
        },
        Cell: props => {
            const { original: data, row } = props;
            const { t } = props.column.getHeaderProps();
            const slots = data
                ? data.initializeWeekDays()
                : data.getDefaultSlots();
            return (
                <ShiftSlots
                    shiftSlots={slots}
                    title={t('workingHours')}
                    testId={`${shiftMasterIds.table.workingDaysCell}-${row._index}`}
                    isBreaksWithOffset
                />
            );
        }
    },
    {
        headerTextId: 'shiftTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                original?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'shiftTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'shiftTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                original?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'shiftTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

const DAY_OF_WEEK_COLUMNS = (() => {
    const days = Object.values(DayOfWeekType);

    // DayOfWeekType has Sunday at the end, so shifting to the front
    const lastDay = days.pop();
    days.unshift(lastDay);

    const dayColumns = [];

    days.forEach(day => {
        dayColumns.push({
            headerTextId: `shiftTable.days.${day}`,
            sortable: false,
            filterable: false,
            id: day,
            minWidth: 130,
            exportId: 'workingDays.slot',
            Cell: props => {
                const {
                    original: { workingDays },
                    tdProps: {
                        rest: { theme, t }
                    }
                } = props;
                const workingDay = workingDays.find(
                    ({ dayOfWeek }) => dayOfWeek === day
                );
                if (!workingDay) {
                    return null;
                }
                const { slot, endDayOffset } = workingDay;
                const { start, end } = slot;
                const translatedStartDay = t(`shiftTable.days.${day}`);
                const endDayIndex =
                    (daysList.indexOf(day) + endDayOffset + 7) % 7;
                const endDay = daysList[endDayIndex];
                const translatedEndDay = t(`shiftTable.days.${endDay}`);
                return (
                    <div className={theme.cellText}>
                        <span className={theme.bold}>
                            {translatedStartDay},{' '}
                        </span>
                        <span>
                            {DateTimeUtils.format(
                                start,
                                { timeStyle: TimeStyle.SHORT },
                                timeFormat
                            )}{' '}
                        </span>
                        <span>- </span>
                        <span className={theme.bold}>{translatedEndDay}, </span>
                        <span>
                            {DateTimeUtils.format(
                                end,
                                { timeStyle: TimeStyle.SHORT },
                                timeFormat
                            )}
                        </span>
                    </div>
                );
            }
        });
    });

    return dayColumns;
})();

const DEFAULT_COLUMNS = [...INITIAL_COLUMNS, ...DAY_OF_WEEK_COLUMNS];

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type ShiftsDataType = {
    shifts: Array<Shift>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    theme: Object,
    data: ShiftsDataType,
    unitConverter: UnitConversionHelper,
    entitySettings: ?EntitySettings,
    tableSettings: ?Array<Object>,
    t: TFunction,
    fetchOptions: () => {},
    isGlobalPersonnel: boolean
};

const shiftMasterContainerFactory = ({
    EntityEditViewContainer = SingleShiftEditContainer,
    EntityImportContainer = ShiftMasterImportContainer,
    EntityExportContainer = MasterExportContainer,
    COLUMNS = DEFAULT_COLUMNS,
    options = {
        entityTableId: ENTITY_TABLE_ID,
        operations: {
            edit: RoleOperationEnum.UPDATE_SHIFT,
            add: RoleOperationEnum.UPDATE_SHIFT,
            export: RoleOperationEnum.EXPORT_SHIFT
        }
    },
    ActionsBar,
    BulkViewEmptyState,
    fetchEntities = ShiftActions.fetchActiveEntities
}) => {
    const ShiftMaster = entityMasterFactory({
        EntityEditViewContainer,
        EntityImportContainer,
        EntityExportContainer,
        COLUMNS,
        options,
        ActionsBar,
        BulkViewEmptyState
    });
    class ShiftMasterContainer extends PureComponent<Props> {
        getTdProps = (state, rowInfo) => {
            if (!rowInfo) {
                return {};
            }
            const {
                original: { id }
            } = rowInfo;
            const {
                t,
                entitySettings,
                selectedRows,
                toggleRowSelection,
                unitConverter,
                theme,
                teams,
                isGlobalPersonnel
            } = this.props;
            const isChecked = selectedRows.indexOf(id) >= 0;
            return {
                onCheckboxClick: () => {
                    toggleRowSelection(id);
                },
                t,
                entitySettings,
                unitConverter,
                theme,
                isChecked,
                className: classnames({ [theme.selectedRow]: isChecked }),
                teams,
                isGlobalPersonnel
            };
        };

        getAdditionalTableProps = () => {
            const { teams } = this.props;
            return {
                teams
            };
        };

        getAdditionalColumns = selectedColumns => {
            const workingDaysIndex = selectedColumns.findIndex(
                col => col === 'workingDays'
            );

            const additionalExportColumns = [
                'workingDays.startLocation',
                'workingDays.endLocation',
                'workingDays.startLatitude',
                'workingDays.endLatitude',
                'workingDays.startLongitude',
                'workingDays.endLongitude',
                'workingDays.breaks'
            ];
            return workingDaysIndex !== -1
                ? [...selectedColumns, ...additionalExportColumns]
                : selectedColumns;
        };

        render() {
            const { clientId, fetchOptions, t } = this.props;
            const shiftFetchOptions = {
                clientId,
                ...(fetchOptions || {})
            };
            return (
                <ShiftMaster
                    {...this.props}
                    getAdditionalColumns={this.getAdditionalColumns}
                    emptyStateImageSrc="/img/masterShift-empty.png"
                    entityNameId={ENTITY_NAME_ID}
                    entityExportId={ENTITY_EXPORT_ID}
                    entityId={'shiftId'}
                    entitySettingsName={'shiftSettings'}
                    fetchOptions={shiftFetchOptions}
                    getTdProps={this.getTdProps}
                    getAdditionalTableProps={this.getAdditionalTableProps}
                    hasFreezableColumns
                    frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                    pinTooltip={t('pinTooltip')}
                    unpinTooltip={t('unpinTooltip')}
                />
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const clientId = clientSelector(state, ownProps);
        const isLoading = ShiftSelectors.isEntitiesLoading(state, ownProps);
        const data = ShiftSelectors.getEntities(state, ownProps);
        const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
        const entityIdToEdit = editEntityIdSelector(state, ownProps);
        const entitySettings = getEntitySettings(state, ownProps);
        const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
        const teams = teamsSelector(state, ownProps);
        const isImportEntityActive = isImportEntityActiveSelector(
            state,
            ownProps
        );
        const isExportEntityActive = isExportEntityActiveSelector(
            state,
            ownProps
        );
        const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);

        return {
            clientId,
            isLoading,
            data,
            isAddEntityActive,
            entityIdToEdit,
            entitySettings,
            tableSettings,
            teams,
            isImportEntityActive,
            isExportEntityActive,
            isGlobalPersonnel
        };
    };

    const withRedux = connect(mapStateToProps, {
        fetchEntities,
        showToastAction: ToastActions.showToast,
        updateToastAction: ToastActions.updateToast,
        hideToastAction: ToastActions.hideToast,
        disableEntity: ShiftActions.disableEntity,
        clearData: ShiftActions.clearData,
        saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
        bulkDisableEntity: ShiftActions.bulkDisableEntity
    });

    const enhance = compose(
        themr(null, theme),
        withRouter,
        withUnitConverter,
        withRedux,
        translate('entityManagement'),
        MasterContainerHOC
    );
    return enhance(ShiftMasterContainer);
};

const ThemedConnectedShiftMasterContainer = shiftMasterContainerFactory({});

export {
    shiftMasterContainerFactory,
    ThemedConnectedShiftMasterContainer as ShiftMasterContainer
};
export default ThemedConnectedShiftMasterContainer;
